import { Api } from '@/Api';
import { DEBUG_NAMESPACE, useDebug } from '@/composables/useDebug';
import { defineStore } from 'pinia';
import { useEntitlementsStore } from '@/store/useEntitlementsStore';
import { useTranscriptionStore } from './useTranscriptionStore';
import { useViewerItemStore } from './useViewerItemStore';

export enum AiSmartActionStatus {
  PENDING = 'pending',
  ENQUEUED = 'enqueued',
  WORKING = 'working',
  DONE = 'done',
  FAILED = 'failed',
}

export interface SmartActionsState {
  primaryCategoryKey?: string;
  content?: string;
  categories?: Category[];
  categoryStatus?: AiSmartActionStatus;
  actionStatus?: AiSmartActionStatus;
  actionType?: string;
  provider?: string;
  feedback?: 'positive' | 'negative' | null;
}
export interface Category {
  key: string;
  subcategories: Array<{ key: string; title: string }>;
}

const { debug } = useDebug(DEBUG_NAMESPACE.SMART_ACTIONS);

const prepareCategoryData = (data: any) => {
  const { categoryStatus, primaryCategory: primaryCategoryKey } = data;

  const formatSubcategories = (subcategories: Record<string, string | undefined>) =>
    Object.keys(subcategories).reduce(
      (acc, key) => {
        const snakeCaseKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
        acc.push({ key: snakeCaseKey, title: subcategories[key] });
        return acc;
      },
      [] as Array<{ key: string; title: string | undefined }>
    );

  const categories = data.categories.map(({ name, subcategories }: { name: string; subcategories: Record<string, string | undefined> }) => ({
    key: name,
    subcategories: formatSubcategories({ ...subcategories }),
  }));

  return {
    primaryCategoryKey,
    categories,
    categoryStatus,
  };
};

export const useSmartActionsStore = defineStore('smartActionsStore', {
  state: () =>
    ({
      primaryCategoryKey: undefined,
      content: undefined,
      categories: [],
      categoryStatus: AiSmartActionStatus.PENDING,
      actionStatus: AiSmartActionStatus.PENDING,
      actionType: undefined,
      provider: undefined,
      feedback: null,
    }) as SmartActionsState,
  actions: {
    initialize(smartActionsData: any) {
      debug('[initialize] Initialize SmartActions store', smartActionsData);
      if (smartActionsData) {
        this.patchSmartActionsData(smartActionsData);
      }
    },

    patchSmartActionsData(data: any) {
      if (!data || !data.attributes) {
        debug('Cannot patch SmartActions data');
        return;
      }

      const patchedData: Partial<SmartActionsState> = {
        content: data.attributes.smartActionData,
        actionStatus: data.attributes.actionStatus,
        actionType: data.attributes.actionType,
        provider: data.attributes.provider,
      };
      const categoryPatchedData = prepareCategoryData(data.attributes);

      this.$patch(state => {
        state.content = patchedData.content;
        state.actionStatus = patchedData.actionStatus;
        state.actionType = patchedData.actionType;
        state.provider = patchedData.provider;
        state.categoryStatus = categoryPatchedData.categoryStatus;
        state.primaryCategoryKey = categoryPatchedData.primaryCategoryKey;
        state.categories = categoryPatchedData.categories;

        // Reset feedback when new smart action is generated
        if (data.attributes.smartActionData) {
          state.feedback = null;
        }
      });
    },

    async requestSmartAction(type: string) {
      const transcriptionStore = useTranscriptionStore();
      const itemStore = useViewerItemStore();
      const entitlementsStore = useEntitlementsStore();

      if (!itemStore.id || transcriptionStore.isTranscriptionMissingContent) {
        return Promise.resolve();
      }

      try {
        this.$patch({ actionType: type, actionStatus: AiSmartActionStatus.ENQUEUED, feedback: null });
        await Api.item.smartActions.create(itemStore.id, type);
        return Promise.resolve();
      } catch (error: any) {
        const status = error?.httpStatus;
        if (status === 403 || status === 429) {
          // Quota exhausted or feature disabled — refresh entitlements to get current state
          entitlementsStore.refreshEntitlements().catch(() => {});
        }
        this.$patch({ actionStatus: AiSmartActionStatus.FAILED });
        debug(`Failed to request smart action for item: ${itemStore.id}`, error);
        return Promise.resolve();
      }
    },
    setSmartActionFeedback(feedback: 'positive' | 'negative' | null) {
      this.$patch({ feedback });
    },
  },
  getters: {
    isCategorizing: (state: SmartActionsState) =>
      state.categoryStatus && [AiSmartActionStatus.ENQUEUED, AiSmartActionStatus.WORKING].indexOf(state.categoryStatus) > -1,
    isActioning: (state: SmartActionsState) =>
      state.actionStatus && state.actionStatus && [AiSmartActionStatus.ENQUEUED, AiSmartActionStatus.WORKING].indexOf(state.actionStatus) > -1,
    isProcessing(): boolean {
      return !!this.isCategorizing || !!this.isActioning;
    },
    getCategories: (state: SmartActionsState) => state.categories,
    getPrimaryCategoryKey: (state: SmartActionsState) => state.primaryCategoryKey,
    getSortedCategories: (state: SmartActionsState) => {
      const primaryCategory = state.categories?.find(category => category.key === state.primaryCategoryKey);
      const otherCategories = state.categories?.filter(category => category.key !== state.primaryCategoryKey) || [];
      return primaryCategory ? [primaryCategory, ...otherCategories] : otherCategories;
    },
  },
});
